import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useDeletePatientPhoto, useUploadPatientPhoto } from "@/lib/mutations";
import { usePatientPhotoBlob } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Camera, Trash, LoaderCircle, User } from "lucide-react";
import { PatientPhotoCaptureDialog } from "./PatientPhotoCaptureDialog";

interface PatientPhotoViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  patientName?: string | null;
  /** Current photo key — the caller's aggregate copy; drives the blob query. */
  photoR2Key: string | null;
  /** Called after a successful upload/remove so the caller can refresh its aggregate. */
  onPhotoChanged?: () => void;
}

/**
 * Photo viewer + editor. Opens from the EMR visit page header (and anywhere
 * a bigger view / quick change is needed). Shows the stored photo large;
 * when the user holds photos.create (no photo yet) or photos.edit (replace)
 * they can capture a new one via the same PatientPhotoCaptureDialog flow as
 * the add-patient form; photos.delete holders can remove it.
 *
 * The blob is refetched here (not shared with the caller's thumb) so both
 * stay correct after mutations; the object-URL lifecycle is owned locally.
 */
export function PatientPhotoViewerDialog({
  open, onOpenChange, patientId, patientName, photoR2Key, onPhotoChanged,
}: PatientPhotoViewerDialogProps) {
  const { isAdmin, permissions } = useAuth();
  const canAdd = isAdmin || permissions.has("photos.create");
  const canReplace = isAdmin || permissions.has("photos.edit");
  const canRemove = isAdmin || permissions.has("photos.delete");

  const [captureOpen, setCaptureOpen] = useState(false);
  const uploadMut = useUploadPatientPhoto();
  const removeMut = useDeletePatientPhoto(patientId);

  const photoQuery = usePatientPhotoBlob(open ? patientId : undefined, photoR2Key);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!photoQuery.data) { setPhotoUrl(null); return; }
    const url = URL.createObjectURL(photoQuery.data);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoQuery.data]);

  const hasPhoto = !!photoR2Key;
  const canCapture = hasPhoto ? canReplace : canAdd;

  const handleConfirm = async (blob: Blob) => {
    await uploadMut.mutateAsync({ patientId, blob });
    onPhotoChanged?.();
  };

  const handleRemove = async () => {
    try {
      await removeMut.mutateAsync();
      onPhotoChanged?.();
      onOpenChange(false);
    } catch { /* toast handled in mutation */ }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Patient photo</DialogTitle>
            <DialogDescription>
              {patientName ? `${patientName} — ` : ""}Passport photo (413 × 531 px, 300 DPI).
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center">
            <div className="flex h-[377px] w-[293px] items-center justify-center overflow-hidden rounded-md border border-cream-200 bg-cream-50 dark:border-dark-700 dark:bg-dark-800">
              {hasPhoto && photoUrl ? (
                <img src={photoUrl} alt="Patient photo" className="h-full w-full object-contain" />
              ) : photoQuery.isFetching ? (
                <LoaderCircle size={32} className="animate-spin text-muted" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-muted">
                  <User size={48} />
                  <span className="text-xs">No photo</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="sm:justify-between">
            <div>
              {hasPhoto && canRemove && (
                <Button variant="ghost" size="sm" className="text-red-600" onClick={handleRemove} disabled={removeMut.isPending}>
                  {removeMut.isPending ? <LoaderCircle size={14} className="mr-1 animate-spin" /> : <Trash size={14} className="mr-1" />}
                  Remove
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {canCapture && (
                <Button size="sm" variant="outline" onClick={() => setCaptureOpen(true)}>
                  <Camera size={14} className="mr-1" /> {hasPhoto ? "Change photo" : "Add photo"}
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PatientPhotoCaptureDialog
        open={captureOpen}
        onOpenChange={setCaptureOpen}
        onConfirm={handleConfirm}
        replacing={hasPhoto}
      />
    </>
  );
}
